/**
 * The per-field retention policy for `user` across a return to the email step.
 *
 * `user` is the order payload: it is posted whole to the order endpoints, so a
 * key that survives an email change is a key attributed to the wrong account.
 * The policy therefore splits on the submitted email rather than merging
 * unconditionally.
 *
 *   same email    -> merge, so a name typed on the profile screen survives the
 *                    return trip
 *   changed email -> rebuild from the submitted user alone, so no neatUserId,
 *                    googleId or profile field from the previous address can
 *                    travel forward
 *
 * `password` is dropped on both arms. It reaches `user` from the profile
 * screen's submit and has no business outliving the step that collected it.
 *
 * It is a pure function over two plain objects, deliberately outside the
 * provider, because the provider has no execution oracle in this repo's jest
 * setup and this is the decision that has to be tested.
 */

/** Emails compare case- and whitespace-insensitively, as the email schema stores them. */
export const isSameEmail = (a, b) => {
  if (!a || !b) return false
  return String(a).trim().toLowerCase() === String(b).trim().toLowerCase()
}

/**
 * Next `user` after an authenticate submit.
 *
 * `previousUser` must be the value read BEFORE validateUser ran. validateUser
 * schedules its own merge onto the same state, and that merge carries the
 * previous address's identity keys forward when the fresh response omits them;
 * passing its result in here would compare the new email against itself and
 * take the merge arm every time.
 */
export const nextUserAfterAuthenticate = (previousUser = {}, submittedUser = {}) => {
  const submitted = { ...submittedUser }
  delete submitted.password

  if (!isSameEmail(previousUser?.email, submitted.email)) return submitted

  const carried = { ...previousUser }
  delete carried.password
  return { ...carried, ...submitted }
}
